import BigNumber from 'bignumber.js'
import { Connection, PublicKey, VersionedTransaction } from '@solana/web3.js'
import { LiquidationAsset, NetworkConfig, TargetCoinLiquidation } from './types'
import { WalletTokenState } from './walletTokenFetcher'

export const DEFAULT_SOLANA_RPC =
  'https://solana-mainnet.g.alchemy.com/v2/kZkfDUn1kdREcw7Nipkjr_FhbYzKwXJh'
const SOLANA_CHAIN_ID = 101
const SOL_MINT = 'So11111111111111111111111111111111111111112'
const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'
const USDT_MINT = 'Es9vMFrzaCERy92gGDUvfJ1uCGj1TnFQ7r5n5Y2w9Aaq'
const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA')

const JUPITER_TOKENS_URL = 'https://lite-api.jup.ag/tokens/v2/tag?query=verified'
// const JUPITER_QUOTE_URL = 'https://quote-api.jup.ag/v6/quote/'
const JUPITER_SWAP_URL = 'https://lite-api.jup.ag/swap/v1/swap-instructions'
const JUPITER_PRICE_URL = 'https://lite-api.jup.ag/price/v3/'

type JupiterToken = {
  address: string
  symbol: string
  name: string
  decimals: number
  logoURI?: string
}

let jupiterTokensCache: JupiterToken[] | null = null

const getSolanaNetworkConfig = (rpcUrl?: string): NetworkConfig => ({
  name: 'Solana',
  rpc: rpcUrl || DEFAULT_SOLANA_RPC,
  chainId: SOLANA_CHAIN_ID,
  chainIdHex: '',
  explorer: 'https://solscan.io',
  queryToken: '',
  symbol: 'SOL',
  baseCoin: 'USDC',
  cmcCoinId: 5426,
  extension: '-SPL',
  decimals: 9,
  transactionPath: '/tx',
  logo: '',
  publicRPC: rpcUrl || DEFAULT_SOLANA_RPC,
  nativeCurrency: {
    name: "Solana",
    symbol: 'SOL',
    cmcCoinId: 5426,
  },
})

const getConnection = (rpcUrl?: string) => new Connection(rpcUrl || DEFAULT_SOLANA_RPC, 'confirmed')

export const getJupiterTokens = async (): Promise<JupiterToken[]> => {
  if (jupiterTokensCache) {
    return jupiterTokensCache
  }
  const res = await fetch(JUPITER_TOKENS_URL)
  if (!res.ok) {
    throw new Error('Failed to load Jupiter token list')
  }
  const tokens = (await res.json()).map(e => {
    return {
      address: e.id,
      name: e.name,
      symbol: e.symbol,
      decimals: e.decimals,
      logoURI: e.icon,
    }
  }) as JupiterToken[]
  jupiterTokensCache = tokens
  return tokens
}

const base64ToBytes = (value: string) => {
  if (typeof atob === 'function') {
    const binary = atob(value)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes
  }
  if (typeof Buffer !== 'undefined') {
    return Uint8Array.from(Buffer.from(value, 'base64'))
  }
  throw new Error('No base64 decoder available')
}

export const fetchJupiterPrices = async (mints: string[]): Promise<Record<string, number>> => {
  if (mints.length === 0) {
    return {}
  }
  const ids = mints.join(',')
  const res = await fetch(`${JUPITER_PRICE_URL}?ids=${encodeURIComponent(ids)}`)
  if (!res.ok) {
    return {}
  }
  const data = await res.json()
  const prices: Record<string, number> = {}
  Object.entries(data || {}).forEach(([mint, value]: any) => {
    prices[mint.toLowerCase()] = Number(value?.usdPrice || 0)
  })
  return prices
}

export const getSolanaMintByTarget = (target: TargetCoinLiquidation) => {
  switch (target) {
    case TargetCoinLiquidation.USDC:
      return USDC_MINT
    case TargetCoinLiquidation.USDT:
      return USDT_MINT
    case TargetCoinLiquidation.NATIVE:
    default:
      return SOL_MINT
  }
}

export const fetchSolanaWalletTokens = async (
  userAddress: string,
  rpcUrl?: string,
  onProgress?: (state: WalletTokenState) => void,
): Promise<WalletTokenState> => {
  const connection = getConnection(rpcUrl)
  const network = getSolanaNetworkConfig(rpcUrl)

  const tokenByNetwork: Record<string, LiquidationAsset[]> = {}
  const tokensList: LiquidationAsset[] = []
  let error: string | undefined

  const chainStatuses = [
    {
      chainId: network.chainId,
      chainName: network.name,
      status: 'loading' as any,
      tokenCount: 0,
      error: undefined as any,
    },
  ]

  const updateProgress = (isLoading: boolean) => {
    onProgress?.({
      tokenByNetwork: { ...tokenByNetwork },
      tokensList: [...tokensList],
      isLoading,
      error,
      chainStatuses: [...chainStatuses],
      completedChains: isLoading ? 0 : 1,
      totalChains: 1,
    })
  }

  updateProgress(true)

  try {
    const pubkey = new PublicKey(userAddress)
    const solBalance = await connection.getBalance(pubkey)
    const solBalanceUi = new BigNumber(solBalance).dividedBy(10 ** 9)
    const solToken: LiquidationAsset = {
      address: SOL_MINT,
      symbol: 'SOL',
      name: 'Solana',
      decimals: 9,
      balance: solBalance.toString(),
      balanceString: solBalanceUi.toFixed(),
      USDBalance: 0,
      stringUSDBalance: '0',
      contract_address: SOL_MINT,
      cmcCoinId: 5426,
      isCustom: false,
      stringBalance: solBalanceUi.toFixed(),
      network,
      nativeCurrency: network.nativeCurrency || { name: 'Solana', symbol: 'SOL', cmcCoinId: 5426 },
      isSelected: false,
      loading: false,
      allocation: 0,
      price: '',
      error: '',
      isNative: true,
    }

    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(pubkey, {
      programId: TOKEN_PROGRAM_ID,
    })
    console.log('tokenAccounts', tokenAccounts)
    const tokenMetadata = await getJupiterTokens()
    console.log('tokenMetadata', tokenMetadata)
    const parsedTokens: LiquidationAsset[] = []
    const mintAddresses: string[] = [SOL_MINT]

    for (const account of tokenAccounts.value) {
      const info: any = account.account.data?.parsed?.info
      const amount = info?.tokenAmount?.amount
      const decimals = info?.tokenAmount?.decimals
      const uiAmount = info?.tokenAmount?.uiAmount
      const mint = info?.mint

      if (
        !mint ||
        amount === undefined ||
        decimals === undefined ||
        uiAmount === undefined ||
        Number(uiAmount) <= 0
      ) {
        continue
      }

      const metadata = tokenMetadata.find((token) => token.address === mint)
      mintAddresses.push(mint)

      parsedTokens.push({
        address: mint,
        symbol: metadata?.symbol || mint.slice(0, 4),
        name: metadata?.name || mint,
        decimals,
        balance: amount,
        balanceString: uiAmount.toString(),
        USDBalance: 0,
        stringUSDBalance: '0',
        contract_address: mint,
        cmcCoinId: 0,
        isCustom: false,
        stringBalance: uiAmount.toString(),
        network,
        nativeCurrency: network.nativeCurrency || { name: 'Solana', symbol: 'SOL', cmcCoinId: 0 },
        isSelected: false,
        loading: false,
        allocation: 0,
        price: '',
        error: '',
        isNative: false,
      })
    }

    const prices = await fetchJupiterPrices(mintAddresses)
    console.log('prices', prices)
    const solPrice = prices[SOL_MINT.toLowerCase()] || 0
    const solUsd = Number(solBalanceUi) * solPrice
    solToken.USDBalance = solUsd
    solToken.stringUSDBalance = solUsd.toString()

    console.log('parsedTokens', parsedTokens)
    parsedTokens.forEach((token) => {
      const price = prices[token.address.toLowerCase()] || 0
      const usdValue = Number(token.balanceString) * price
      console.log(token.address, 'usdValue', usdValue)
      token.USDBalance = usdValue
      token.stringUSDBalance = usdValue.toString()
      tokensList.push({ ...token, price: price.toString() })
    })

    if (Number(solBalanceUi) > 0) {
      tokensList.unshift({
        ...solToken,
        USDBalance: solUsd,
        stringUSDBalance: solUsd.toString(),
        price: solPrice.toString(),
      })
    }

    console.log('tokensList', tokensList)
    console.log('chainStatuses', chainStatuses)
    tokenByNetwork[network.chainId.toString()] = tokensList
    chainStatuses[0].status = 'completed'
    chainStatuses[0].tokenCount = tokensList.length
  } catch (err) {
    error = err instanceof Error ? err.message : 'Unknown error'
    tokenByNetwork[network.chainId.toString()] = []
    chainStatuses[0].status = 'error'
    chainStatuses[0].error = error
  }

  updateProgress(false)

  return {
    tokenByNetwork,
    tokensList,
    isLoading: false,
    error,
    chainStatuses,
    completedChains: 1,
    totalChains: 1,
  }
}

export const getSolanaTokenBalance = async (
  userAddress: string,
  token: LiquidationAsset,
  rpcUrl?: string,
): Promise<{ balance: string; decimals: number }> => {
  const connection = getConnection(rpcUrl)
  const pubkey = new PublicKey(userAddress)
  if (token.isNative) {
    const balance = await connection.getBalance(pubkey)
    return { balance: balance.toString(), decimals: 9 }
  }

  const tokenAccounts = await connection.getParsedTokenAccountsByOwner(pubkey, {
    programId: TOKEN_PROGRAM_ID,
  })

  for (const account of tokenAccounts.value) {
    const info: any = account.account.data?.parsed?.info
    if (info?.mint === token.address) {
      const amount = info?.tokenAmount?.amount || '0'
      const decimals = info?.tokenAmount?.decimals || token.decimals
      return { balance: amount, decimals }
    }
  }

  return { balance: '0', decimals: token.decimals }
}

export const getSolanaQuote = async ({
  inputMint,
  outputMint,
  amount,
  slippageBps,
  platformFeeBps,
  swapMode,
}: {
  inputMint: string
  outputMint: string
  amount: string
  slippageBps: number
  platformFeeBps?: number
  swapMode?: 'ExactOut' | 'ExactIn'
}) => {
  const _swapMode = swapMode ?? 'ExactIn'
  const url = `https://lite-api.jup.ag/swap/v1/quote?inputMint=${inputMint}&outputMint=${outputMint}&amount=${amount}&${
    platformFeeBps ? `platformFeeBps=${platformFeeBps}&` : ''
  }slippageBps=${slippageBps}&swapMode=${_swapMode}&asLegacyTransaction=false&onlyDirectRoutes=false`

  const res = await fetch(url)
  if (!res.ok) {
    throw new Error('Failed to fetch Jupiter quote')
  }
  const data = await res.json()
  console.log('data', data)
  return data || null
}

export const getSolanaSwapTransaction = async ({
  quote,
  userPublicKey,
  feeAccount
}: {
  quote: any
  userPublicKey: string
  feeAccount:PublicKey
}) => {
  const res = await fetch(JUPITER_SWAP_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      quoteResponse: quote,
      userPublicKey,
      wrapAndUnwrapSol: true,
      prioritizationFeeLamports: {
        priorityLevelWithMaxLamports: {
          maxLamports: 10000000,
          priorityLevel: 'high',
        },
      },
      feeAccount,
    }),
  })

  if (!res.ok) {
    throw new Error('Failed to build Jupiter swap')
  }

  return res.json()
}

export const deserializeSwapTransaction = (swapTransaction: string) => {
  return VersionedTransaction.deserialize(base64ToBytes(swapTransaction))
}

// async function getTokenByMint (tokenAddress: string): Promise<any> {
//   const res = await axios.get<{ data: TokenByMint }>(
//     `${getFlag(Flags.provider_base_url)}/tokens/v1/solana:101/address/${tokenAddress}`,
//   )
//   const data = res.data.data
//   return {
//     logo: data.logoURI,
//     decimals: data.decimals,
//     symbol: data.symbol,
//     contractAddress: tokenAddress,
//     name: data.name,
//     cmcId: undefined,
//   }
// }